import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo, LogoMark } from "@/components/Logo";
import { CheckCircle2, Clock, AlertCircle, MessageCircle, Link2, ArrowRight } from "lucide-react";

const SLIDES = [
  { render: () => (
    <div className="flex flex-col items-center text-center">
      <LogoMark size={84} />
      <h1 className="mt-6 font-display text-4xl font-extrabold text-primary">Debii</h1>
      <p className="mt-2 font-display text-lg font-semibold text-foreground">Never forget who owes you.</p>
      <p className="mt-4 text-sm text-muted-foreground max-w-xs leading-relaxed">
        Keep track of money you've lent to friends, family and others — all in one simple place.
      </p>
    </div>
  )},
  { render: () => (
    <div className="flex flex-col items-center text-center w-full">
      <h2 className="font-display text-2xl font-bold text-foreground">Record it in seconds</h2>
      <p className="mt-3 text-sm text-muted-foreground max-w-xs leading-relaxed">
        Add who owes you, how much they owe and when they promised to pay.
      </p>
      <div className="mt-8 w-full max-w-xs bg-card rounded-2xl border border-border p-4 text-left shadow-sm animate-rise">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-full bg-secondary text-primary font-display font-bold flex items-center justify-center">T</div>
          <div className="flex-1">
            <p className="font-semibold">Tunde</p>
            <p className="text-xs text-muted-foreground">Due 30 September</p>
          </div>
          <p className="font-display font-bold tabular">₦20,000</p>
        </div>
      </div>
    </div>
  )},
  { render: () => (
    <div className="flex flex-col items-center text-center w-full">
      <h2 className="font-display text-2xl font-bold text-foreground">We'll help you remember</h2>
      <div className="mt-6 w-full max-w-xs bg-primary text-primary-foreground rounded-3xl p-6 text-left shadow-lg animate-rise">
        <p className="text-xs opacity-80">Total owed to you</p>
        <p className="font-display text-4xl font-extrabold tabular mt-1">₦85,000</p>
        <div className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between"><span>Tunde — ₦20,000</span><span className="opacity-80">Due tomorrow</span></div>
          <div className="flex justify-between"><span>Amaka — ₦35,000</span><span className="text-red-300">5 days overdue</span></div>
        </div>
      </div>
      <p className="mt-6 text-sm text-muted-foreground max-w-xs leading-relaxed">
        See what's outstanding, what's due soon and what's overdue without keeping everything in your head.
      </p>
    </div>
  )},
  { render: () => (
    <div className="flex flex-col items-center text-center w-full">
      <div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center"><MessageCircle className="h-7 w-7 text-primary" /></div>
      <h2 className="mt-5 font-display text-2xl font-bold text-foreground">Remind without the awkwardness</h2>
      <p className="mt-3 text-sm text-muted-foreground max-w-xs leading-relaxed">
        Debii helps you create a polite reminder when it's time to ask for your money back.
      </p>
      <div className="mt-6 w-full max-w-xs bg-card rounded-2xl border border-border p-4 text-left text-sm text-muted-foreground animate-rise">
        "Hi Tunde, just a quick reminder about the ₦20,000. Please let me know when you'll be able to send it. Thanks."
      </div>
    </div>
  )},
  { render: () => (
    <div className="flex flex-col items-center text-center w-full">
      <div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center"><Link2 className="h-7 w-7 text-primary" /></div>
      <h2 className="mt-5 font-display text-2xl font-bold text-foreground">Keep a shared record</h2>
      <p className="mt-3 text-sm text-muted-foreground max-w-xs leading-relaxed">
        Send a simple acknowledgement request so both of you remember what was agreed.
      </p>
      <div className="mt-6 flex items-center gap-2 text-xs font-semibold">
        <span className="rounded-full bg-secondary px-3 py-1.5">You record</span>
        <ArrowRight className="h-4 w-4 text-muted-foreground" />
        <span className="rounded-full bg-secondary px-3 py-1.5">Send link</span>
        <ArrowRight className="h-4 w-4 text-muted-foreground" />
        <span className="rounded-full bg-emerald-100 text-emerald-800 px-3 py-1.5">They acknowledge ✓</span>
      </div>
      <p className="mt-5 text-xs text-muted-foreground">They don't need Debii to acknowledge it.</p>
    </div>
  )},
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [i, setI] = useState(0);
  const isLast = i === SLIDES.length - 1;

  const goAuth = (mode) => {
    localStorage.setItem("debii_onboarded", "1");
    navigate(`/auth?mode=${mode}`);
  };

  return (
    <div className="debii-shell flex flex-col px-6 py-8">
      <div className="flex items-center justify-between">
        <Logo size={26} />
        {!isLast && (
          <button data-testid="onboarding-skip" onClick={() => goAuth("register")} className="text-sm font-medium text-muted-foreground">
            Skip
          </button>
        )}
      </div>

      <div className="flex-1 flex items-center justify-center py-8">
        <div key={i} className="animate-rise w-full">{SLIDES[i].render()}</div>
      </div>

      <div className="flex justify-center gap-1.5 mb-6">
        {SLIDES.map((_, idx) => (
          <span key={idx} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-primary" : "w-1.5 bg-border"}`} />
        ))}
      </div>

      {isLast ? (
        <div className="space-y-3">
          <p className="text-center font-display text-lg font-bold text-foreground">Your memory doesn't have to do all the work.</p>
          <p className="text-center text-sm text-muted-foreground">Next time someone says "I'll pay you back," put it on Debii.</p>
          <Button data-testid="onboarding-create-account" onClick={() => goAuth("register")} className="w-full h-12 rounded-xl text-base">
            Create My Account
          </Button>
          <Button data-testid="onboarding-signin" variant="ghost" onClick={() => goAuth("login")} className="w-full h-11 rounded-xl">
            Sign In
          </Button>
          <p className="text-center text-xs text-muted-foreground">Free to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          <Button data-testid="onboarding-next" onClick={() => setI(i + 1)} className="w-full h-12 rounded-xl text-base">
            {i === 0 ? "Get Started" : "Continue"}
          </Button>
          <Button data-testid="onboarding-have-account" variant="ghost" onClick={() => goAuth("login")} className="w-full h-11 rounded-xl">
            I already have an account
          </Button>
        </div>
      )}
    </div>
  );
}
